import { FC, useState } from 'react';

import Stack from '@mui/material/Stack/Stack';

import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import Details from '@/shared/components/panels/CostsDetails/Details';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import ProductsSearchBar from '@/shared/components/widgets/ProductsSearchBar/ProductsSearchBar';
import WaveUISortableContainer from '@/shared/components/widgets/WaveUISortableContainer';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';

import Cart from '../components/Cart/Cart';
import useCart from '../components/Cart/hooks/useCart';
import Tile from '../components/Tile/Tile';
import TileType from '../components/Tile/types/tile.type';
import tiles from '../config/tiles.config';
import AddDiscountDialog from './components/AddDiscountDialog';
import AddTileDialog from './components/AddTileDialog';
import OrderCreateDialog from './components/OrderCreateDialog';
import SearchProductNotFound from './components/SearchProductNotFound';
import ShipToCustomerDialog from './components/ShipToCustomerDialog';
import TileConfigDialog from './components/TileConfigDialog';
import TileCustomerAssignDialog from './components/TileCustomerAssignDialog';
import Title from './components/Title';
import WarningMessage from './components/WarningMessage';
import useCheckoutMain from './hooks/useCheckoutMain';
import useDefaultSettings from './hooks/useDefaultSettings';
import useQuotes from './hooks/useQuotes';

const CheckoutMain: FC = () => {
  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const cartState = useCart();

  const [checkoutCurrency, setCheckoutCurrency] = useState('CAD');

  const defaultSettingsState = useDefaultSettings({ checkoutCurrency });

  const { searchOverProductsState, tileState, isWarningVisible } =
    useCheckoutMain({
      dialogProps: {
        onUpdateDialogState,
        onUpdateNotificationBar,
        onUpdateProgressDialog,
      },
      defaultSettingsState,
      cartState,
    });

  const { totalCostInfo, onOrderCreate, onQuoteUpdate } = useQuotes({
    cartState,
    defaultSettingsState,
    dialogProps: {
      onUpdateDialogState,
      onUpdateNotificationBar,
      onUpdateProgressDialog,
    },
    currency: checkoutCurrency,
  });

  return (
    <div>
      <Title
        isLoading={false}
        currency={checkoutCurrency}
        productsInCartLength={cartState.productsInCart.length}
        assignedCustomer={cartState?.assignedCustomer || null}
        onClickAssignedCustomer={() => {
          onUpdateDialogState(DialogName.AssignCustomer, {
            open: true,
          });

          tileState.tileActionHandlers['assign-customer'].trigger();
        }}
        onClearCart={cartState.clearCart}
        onCheckout={() => {
          onUpdateDialogState(DialogName.ConfirmOrder, {
            open: true,
            errors: [],
          });

          tileState.tileActionHandlers['assign-customer'].trigger(true);
        }}
        onCurrencyChange={(value) => setCheckoutCurrency(value)}
      />

      <Stack
        sx={{
          gap: 2,
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr  minmax(500px, 1fr)',
          alignItems: 'flex-start',
        }}
      >
        {isWarningVisible && (
          <Stack sx={{ gridColumn: '1/-1' }}>
            <WarningMessage settings={defaultSettingsState} />
          </Stack>
        )}

        <Stack spacing={2}>
          <ProductsSearchBar
            productsSearchBarState={searchOverProductsState}
            externalFields={{ currency: checkoutCurrency }}
          />

          <WaveUISortableContainer
            items={tileState.tilesList}
            getUpdatedItems={tileState.setTilesList}
            onClickOutsideCallback={() => tileState.setTilesMode('idle')}
            componentRenderer={({ props: tile, index, columnsLength }) => {
              const actionKey =
                tile.action as keyof typeof tileState.tileActionHandlers;
              const idx = index || 0;

              return (
                <Tile
                  {...tile}
                  index={idx + 1}
                  isAddTileItem={actionKey === 'add-tile'}
                  mode={tileState.tilesMode}
                  columnsLength={columnsLength}
                  containerProps={{
                    ...tileState.longPressAttrs,
                    onClick: () => {
                      if (tileState.tilesMode === 'edit') return;

                      tileState.tileActionHandlers[actionKey].trigger();
                    },
                  }}
                  onEditTile={() => {
                    onUpdateDialogState(DialogName.TileConfig, {
                      open: true,
                      data: tile,
                    });
                  }}
                  onRemoveTile={() => {
                    tileState.handleRemoveTile(tile?.id || '');
                  }}
                />
              );
            }}
          />
        </Stack>

        <Stack spacing={2}>
          <Cart
            productListProps={{
              productsInCart: cartState.productsInCart,
              removeProductFromCart: cartState.removeProductFromCart,
              updateProductInCart: cartState.updateProduct,
            }}
            titleProps={{ ...searchOverProductsState.barcodeScannerProps }}
            currency={checkoutCurrency}
          />

          <Details
            shippingPrice={totalCostInfo.shipping_price}
            orderTotal={totalCostInfo.order_total}
            subTotal={totalCostInfo.sub_total}
            currency={checkoutCurrency}
            discount={totalCostInfo.discount}
            tax={totalCostInfo.tax}
          />
        </Stack>
      </Stack>

      {dialog?.addTile?.open && (
        <AddTileDialog
          tiles={tiles}
          selectedTiles={tileState.tilesList}
          errors={dialog.addTile?.errors}
          onAddTile={(newTile) => {
            tileState.handleAddTile(newTile);
            onUpdateDialogState(DialogName.AddTile, {
              open: false,
            });
          }}
          onOpenTileConfigForm={(tile) => {
            onUpdateDialogState(DialogName.TileConfig, {
              open: true,
              data: tile,
            });
          }}
          onClose={() =>
            onUpdateDialogState(DialogName.AddTile, { open: false })
          }
        />
      )}

      {dialog?.tileConfig?.open && (
        <TileConfigDialog
          tile={dialog.tileConfig.data as TileType}
          onAddTile={(tile: TileType) => {
            tileState.handleAddTile(tile);
            onUpdateDialogState(DialogName.AddTile, {
              open: false,
            });
            onUpdateDialogState(DialogName.TileConfig, {
              open: false,
              data: null,
            });
          }}
          onClose={() => {
            onUpdateDialogState(DialogName.TileConfig, {
              open: false,
              data: null,
            });
          }}
        />
      )}

      {dialog?.assignCustomer?.open && (
        <TileCustomerAssignDialog
          searchBoxProps={
            tileState.tileActionHandlers['assign-customer'].searchBoxProps
          }
          loaders={{
            updateQuote: dialog.assignCustomer?.loading,
            searchCustomer: false,
          }}
          onClose={() => {
            onUpdateDialogState(DialogName.AssignCustomer, {
              open: false,
            });
          }}
          proceed={() => {
            void onQuoteUpdate({ currency: checkoutCurrency });
          }}
        />
      )}

      {dialog?.searchProductNotFound?.open && (
        <SearchProductNotFound
          onClose={() =>
            onUpdateDialogState(DialogName.SearchProductNotFound, {
              open: false,
            })
          }
        />
      )}

      {dialog?.confirmOrder?.open && (
        <OrderCreateDialog
          loading={dialog?.confirmOrder?.loading}
          errors={dialog?.confirmOrder?.errors}
          searchBoxProps={
            tileState.tileActionHandlers['assign-customer'].searchBoxProps
          }
          onClose={() =>
            onUpdateDialogState(DialogName.ConfirmOrder, {
              open: false,
            })
          }
          proceed={() => {
            void onOrderCreate();
          }}
        />
      )}

      {dialog?.shipToCustomer?.open && (
        <ShipToCustomerDialog
          shipToCustomerState={tileState.tileActionHandlers['ship-to-customer']}
          loading={dialog?.shipToCustomer?.loading}
          onClose={() => {
            onUpdateDialogState(DialogName.ShipToCustomer, {
              open: false,
            });
          }}
          proceed={(value) => {
            void onQuoteUpdate({ ...value, currency: checkoutCurrency });
          }}
        />
      )}

      {dialog?.addDiscount?.open && (
        <AddDiscountDialog
          addDiscountDialogState={tileState.tileActionHandlers['add-discount']}
          loading={dialog?.addDiscount?.loading}
          onClose={() => {
            onUpdateDialogState(DialogName.AddDiscount, {
              open: false,
            });
          }}
          proceed={(discount) => {
            void onQuoteUpdate({
              discount_percent: discount,
              currency: checkoutCurrency,
            });
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </div>
  );
};

export default CheckoutMain;
